import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FileDown, Trash2, Bookmark, PencilLine, Dices, AlertCircle, Printer, ListOrdered } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLottery } from "@/hooks/use-lottery-selection";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  buildGameListPrintHtml,
  buildOfficialVolantePrintHtml,
  generateGameListPDF,
  generateOfficialVolantePDF,
  getOfficialVolantePageCount,
  OFFICIAL_VOLANTE_LAYOUTS,
} from "@/lib/volante-pdf";


type SavedGame = { id: string; numbers: number[]; label: string | null; created_at: string };

const PRICE_BY_LOTTERY: Record<string, number> = {
  "mega-sena": 6,
  lotofacil: 3.5,
  quina: 3,
  "dupla-sena": 3,
};

type ExportMode = "list" | "official";

const ExportarContent = () => {
  const { selected, config } = useLottery();
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<"manual" | "saved" | "imported">("manual");
  const [manualText, setManualText] = useState("");
  const [selectedSaved, setSelectedSaved] = useState<Set<string>>(new Set());
  const [importedGames, setImportedGames] = useState<number[][]>([]);
  const [mode, setMode] = useState<ExportMode>("list");
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const officialLotterySlug = selected as "mega-sena" | "lotofacil" | "quina" | "dupla-sena";
  const officialLayout = OFFICIAL_VOLANTE_LAYOUTS[officialLotterySlug];

  // Recebe jogos vindos do Gerador via state
  useEffect(() => {
    const incoming = (location.state as { games?: number[][] } | null)?.games;
    if (incoming && incoming.length > 0) {
      setImportedGames(incoming);
      setSource("imported");
    }
  }, [location.state]);

  const { data: savedGames = [] } = useQuery({
    queryKey: ["saved-games", selected, user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data: lottery } = await supabase.from("lotteries").select("id").eq("slug", selected).maybeSingle();
      if (!lottery) return [];
      const { data, error } = await supabase
        .from("saved_games")
        .select("id, numbers, label, created_at")
        .eq("user_id", user.id)
        .eq("lottery_id", lottery.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SavedGame[];
    },
    enabled: !!user,
  });

  const parsedManual = useMemo(() => parseManualGames(manualText, config), [manualText, config]);

  const gamesToExport: number[][] = useMemo(() => {
    if (source === "imported") return importedGames;
    if (source === "manual") return parsedManual.valid;
    return savedGames.filter((g) => selectedSaved.has(g.id)).map((g) => g.numbers);
  }, [source, importedGames, parsedManual.valid, savedGames, selectedSaved]);
  const officialPageCount = getOfficialVolantePageCount(officialLotterySlug, gamesToExport.length);

  const handleDeleteSaved = async (id: string) => {
    const { error } = await supabase.from("saved_games").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro ao remover", description: error.message, variant: "destructive" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["saved-games"] });
    setSelectedSaved((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    toast({ title: "Jogo removido" });
  };

  const handleExport = (action: "save" | "print" = "save") => {
    if (gamesToExport.length === 0) {
      toast({ title: "Nenhum jogo selecionado", variant: "destructive" });
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    let doc;
    let filename: string;
    if (mode === "list") {
      doc = generateGameListPDF({
        lotteryName: config.name,
        games: gamesToExport,
        costPerGame: PRICE_BY_LOTTERY[selected],
        brandLine: "LotoTerminal",
      });
      filename = `jogos-${selected}-${today}.pdf`;
    } else {
      doc = generateOfficialVolantePDF({
        lotterySlug: officialLotterySlug,
        games: gamesToExport,
        offsetX,
        offsetY,
      });
      filename = `volante-oficial-${selected}-${today}.pdf`;
    }

    if (action === "save") {
      doc.save(filename);
      toast({
        title: mode === "list" ? "Lista exportada" : "Volante oficial gerado",
        description:
          mode === "list"
            ? `${gamesToExport.length} jogo(s) prontos para conferência.`
            : `${officialPageCount} volante(s) gerado(s). Coloque os volantes oficiais na impressora.`,
      });
    } else {
      const html =
        mode === "official"
          ? buildOfficialVolantePrintHtml({ lotterySlug: officialLotterySlug, games: gamesToExport, offsetX, offsetY })
          : buildGameListPrintHtml({
              lotteryName: config.name,
              games: gamesToExport,
              costPerGame: PRICE_BY_LOTTERY[selected],
              brandLine: "LotoTerminal",
            });
      const existing = document.getElementById("loto-print-frame");
      if (existing) existing.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "loto-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.opacity = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);
      const printDoc = iframe.contentWindow?.document;
      if (!printDoc) {
        doc.save(filename);
        toast({ title: "PDF gerado", description: "Não foi possível abrir a impressão direta; abra o PDF e imprima manualmente." });
        return;
      }
      printDoc.open();
      printDoc.write(html);
      printDoc.close();
      toast({
        title: mode === "list" ? "Abrindo impressão da lista" : "Abrindo impressão do volante oficial",
        description:
          mode === "list"
            ? "Confirme as configurações no diálogo de impressão do navegador."
            : `${gamesToExport.length} jogo(s) em ${officialPageCount} volante(s). Use o tamanho oficial do volante, retrato e escala 100%.`,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Exportar jogos — {config.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gere uma lista compacta para conferência ou imprima as marcações diretamente nos volantes oficiais da Caixa.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("save")}
            disabled={gamesToExport.length === 0}
            className="border-lt-border"
          >
            <FileDown className="mr-2 h-4 w-4" /> Salvar PDF ({gamesToExport.length})
          </Button>
          <Button
            onClick={() => handleExport("print")}
            disabled={gamesToExport.length === 0}
            className="bg-primary text-primary-foreground hover:opacity-90"
          >
            <Printer className="mr-2 h-4 w-4" /> Imprimir ({gamesToExport.length})
          </Button>
        </div>
      </div>

      <Card className="border-lt-border bg-card">
        <CardHeader>
          <CardTitle className="font-serif text-lg">Modo de exportação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as ExportMode)} className="grid gap-3 sm:grid-cols-2">
            <label
              htmlFor="mode-list"
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                mode === "list" ? "border-primary bg-primary/5" : "border-lt-border bg-background/40"
              }`}
            >
              <RadioGroupItem value="list" id="mode-list" className="mt-0.5" />
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ListOrdered className="h-4 w-4 text-primary" /> Lista compacta
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  PDF enxuto com a numeração de todos os jogos. Ideal para conferência.
                </p>
              </div>
            </label>
            <label
              htmlFor="mode-official"
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                mode === "official" ? "border-primary bg-primary/5" : "border-lt-border bg-background/40"
              }`}
            >
              <RadioGroupItem value="official" id="mode-official" className="mt-0.5" />
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Printer className="h-4 w-4 text-primary" /> Volante oficial
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Imprime apenas as marcações no volante físico: até {officialLayout.playsPerSlip} jogos por volante.
                </p>
              </div>
            </label>
          </RadioGroup>

          {mode === "official" && (
            <>
              <Alert className="border-primary/30 bg-primary/5">
                <Printer className="h-4 w-4 text-primary" />
                <AlertTitle>Antes de imprimir</AlertTitle>
                <AlertDescription className="text-xs leading-relaxed">
                  Coloque {officialPageCount} volante{officialPageCount !== 1 ? "s" : ""} oficial{officialPageCount !== 1 ? "is" : ""} da {config.name} em branco na bandeja da impressora (papel <strong>{officialLayout.paperW}×{officialLayout.paperH} mm</strong>),
                  com a face de marcação para cima e o cabeçalho voltado para a entrada do papel. No diálogo de impressão selecione <strong>tamanho personalizado {officialLayout.paperW}×{officialLayout.paperH} mm, retrato, escala 100% (sem ajuste)</strong>.
                  Cada volante recebe até {officialLayout.playsPerSlip} jogo{officialLayout.playsPerSlip !== 1 ? "s" : ""}: se houver {officialLayout.playsPerSlip + 1} jogos, o último sai no volante seguinte.
                  Imprima 1 página de teste em folha comum primeiro e, se necessário, use os ajustes finos abaixo para alinhar.
                </AlertDescription>
              </Alert>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Ajuste horizontal (mm)</Label>
                  <Input
                    type="number"
                    step={0.5}
                    value={offsetX}
                    onChange={(e) => setOffsetX(Number(e.target.value) || 0)}
                    className="mt-1 border-lt-border bg-background"
                  />
                  <p className="mt-1 text-[10px] text-muted-foreground">Positivo move marcas para a direita.</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Ajuste vertical (mm)</Label>
                  <Input
                    type="number"
                    step={0.5}
                    value={offsetY}
                    onChange={(e) => setOffsetY(Number(e.target.value) || 0)}
                    className="mt-1 border-lt-border bg-background"
                  />
                  <p className="mt-1 text-[10px] text-muted-foreground">Positivo move marcas para baixo.</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Tabs value={source} onValueChange={(v) => setSource(v as typeof source)}>
        <TabsList className="bg-card border border-lt-border">
          <TabsTrigger value="manual"><PencilLine className="mr-1.5 h-3.5 w-3.5" /> Manual</TabsTrigger>
          <TabsTrigger value="saved"><Bookmark className="mr-1.5 h-3.5 w-3.5" /> Salvos ({savedGames.length})</TabsTrigger>
          <TabsTrigger value="imported" disabled={importedGames.length === 0}>
            <Dices className="mr-1.5 h-3.5 w-3.5" /> Do Gerador ({importedGames.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="mt-4">
          <Card className="border-lt-border bg-card">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Digite ou cole seus jogos</CardTitle>
              <p className="text-xs text-muted-foreground">
                Um jogo por linha. Separe os números por espaço, vírgula ou hífen. Entre {config.minPick} e {config.maxPick} dezenas (de {config.min} a {config.max}).
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                placeholder={`Ex.: ${Array.from({ length: config.minPick }, (_, i) => String(i * 3 + 1).padStart(2, "0")).join(" - ")}`}
                rows={8}
                className="font-mono text-sm border-lt-border bg-background"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {parsedManual.valid.length} jogo{parsedManual.valid.length !== 1 ? "s" : ""} válido{parsedManual.valid.length !== 1 ? "s" : ""}
                </span>
                {parsedManual.errors.length > 0 && (
                  <span className="flex items-center gap-1 text-destructive">
                    <AlertCircle className="h-3 w-3" /> {parsedManual.errors.length} linha{parsedManual.errors.length !== 1 ? "s" : ""} com erro
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="saved" className="mt-4">
          <Card className="border-lt-border bg-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="font-serif text-lg">Jogos salvos</CardTitle>
              {savedGames.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setSelectedSaved(
                      selectedSaved.size === savedGames.length ? new Set() : new Set(savedGames.map((g) => g.id)),
                    )
                  }
                >
                  {selectedSaved.size === savedGames.length ? "Desmarcar todos" : "Selecionar todos"}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {savedGames.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Nenhum jogo salvo ainda. Use o Gerador e clique em "Salvar jogos" para guardar aqui.
                </p>
              ) : (
                <div className="space-y-2">
                  {savedGames.map((g) => {
                    const isSelected = selectedSaved.has(g.id);
                    return (
                      <div
                        key={g.id}
                        onClick={() => {
                          setSelectedSaved((prev) => {
                            const next = new Set(prev);
                            if (next.has(g.id)) next.delete(g.id);
                            else next.add(g.id);
                            return next;
                          });
                        }}
                        className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors ${
                          isSelected ? "border-primary bg-primary/5" : "border-lt-border bg-background/40 hover:bg-background/70"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="accent-primary"
                          />
                          <div>
                            <div className="flex flex-wrap gap-1">
                              {g.numbers.map((n) => (
                                <span key={n} className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-lt-gold-border bg-lt-gold-bg font-mono text-[10px]">
                                  {String(n).padStart(2, "0")}
                                </span>
                              ))}
                            </div>
                            {g.label && <p className="mt-1 text-xs text-muted-foreground">{g.label}</p>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSaved(g.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="imported" className="mt-4">
          <Card className="border-lt-border bg-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="font-serif text-lg">Jogos importados do Gerador</CardTitle>
              <Badge variant="outline">{importedGames.length} jogos</Badge>
            </CardHeader>
            <CardContent>
              {importedGames.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Nenhum jogo importado. Vá ao Gerador e clique em "Exportar PDF" para trazer os jogos aqui.
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-auto">
                  {importedGames.map((g, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-md border border-lt-border bg-background/40 px-3 py-2">
                      <span className="w-8 font-mono text-xs text-muted-foreground">#{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex flex-wrap gap-1">
                        {g.map((n) => (
                          <span key={n} className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-lt-gold-border bg-lt-gold-bg font-mono text-[10px]">
                            {String(n).padStart(2, "0")}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

function parseManualGames(text: string, config: { min: number; max: number; minPick: number; maxPick: number }) {
  const valid: number[][] = [];
  const errors: string[] = [];
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    const nums = line.split(/[\s,;\-]+/).map((s) => parseInt(s, 10)).filter((n) => !isNaN(n));
    const unique = [...new Set(nums)];
    const inRange = unique.every((n) => n >= config.min && n <= config.max);
    if (unique.length >= config.minPick && inRange && unique.length <= config.maxPick) {
      valid.push(unique.sort((a, b) => a - b));
    } else {
      errors.push(line);
    }
  }
  return { valid, errors };
}

export default ExportarContent;
